import React, { useMemo, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { createTicket, createKontrolTicket } from "../request/request";
import { useServiceSelection } from "../hooks/useServiceSelection";
import { useLoadServiceItems } from "../hooks/useLoadServiceItems";
import {
	extractUniqueCustomers,
	filterServicesByCustomer,
	getEndCustomerId,
	getServiceSelectionError,
	isServiceSelectionValid,
} from "../utils/tickets";
import { KontrolEdgeServiceSelection } from "./shared/KontrolEdgeServiceSelection";
import { RequestModalShell, type RequestStep } from "./requestForm/RequestModalShell";
import { SummaryFields } from "./requestForm/SummaryFields";
import { AttachmentDropzone } from "./requestForm/AttachmentDropzone";
import { ContactResponseFields } from "./requestForm/ContactResponseFields";
import {
	createEmptyContact,
	isContactValid,
	toContactPayload,
} from "./requestForm/contactDetails";
import { isDescriptionValid, isTitleValid } from "./requestForm/fieldRules";
import { useRequestSteps } from "./requestForm/useRequestSteps";
import { useTicketSubmission } from "./requestForm/useTicketSubmission";
import {
  NewTicketParticipants,
  addParticipantsOnceCreated,
  useNewTicketParticipants,
} from "./requestForm/NewTicketParticipants";

interface HelpRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	onTicketCreated?: () => void;
}

// Help Request has always defaulted to Mexico, unlike the other modals.
const HELP_DEFAULT_COUNTRY_CODE = "+52";
const emptyHelpContact = () => createEmptyContact(HELP_DEFAULT_COUNTRY_CODE);

// Service selection only exists for Kontrol Edge: it is the step that ties the
// ticket to the inventory, and only KS carries the Service ID field (CTX-533).
const KE_STEPS: RequestStep[] = [
	{ id: "services", title: "Services" },
	{ id: "details", title: "Details" },
	{ id: "attachments", title: "Files & people" },
];
const REFLECTOR_STEPS: RequestStep[] = [
	{ id: "details", title: "Details" },
	{ id: "attachments", title: "Attachments & contact" },
];

export const HelpRequestModal: React.FC<HelpRequestModalProps> = ({
	isOpen,
	onClose,
	onTicketCreated,
}) => {
	const api = useAxiosAuth0();
	const { tenantId, partnerId } = useAppSelector((state) => state.selection);
	const isPartnerView = tenantId === partnerId;
	const globalProduct = useAppSelector(
		(state) => state.product.selectedProduct
	);
	const isKontrolEdge = globalProduct === "Kontrol Edge";

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
	const [contact, setContact] = useState(emptyHelpContact);
	const submission = useTicketSubmission(onTicketCreated);

	const serviceSelection = useServiceSelection();
	const { items: serviceItems } = useLoadServiceItems(
		api,
		tenantId,
		isKontrolEdge
	);
	const uniqueCustomers = useMemo(
		() => extractUniqueCustomers(serviceItems),
		[serviceItems]
	);
	// Outside partner view the user only ever sees their own services, so there
	// is nothing to narrow down.
	const filteredServiceItems = useMemo(
		() =>
			isPartnerView
				? filterServicesByCustomer(
						serviceItems,
						serviceSelection.selectedCustomerId
					)
				: serviceItems,
		[serviceItems, serviceSelection.selectedCustomerId, isPartnerView]
	);

	const endCustomerId = getEndCustomerId(

		isPartnerView,

		serviceSelection.selectedCustomerId,

		tenantId

	);

	const { participants, setParticipants, resetParticipants } =

		useNewTicketParticipants(isKontrolEdge ? endCustomerId : undefined);

	const steps = isKontrolEdge ? KE_STEPS : REFLECTOR_STEPS;
	const stepper = useRequestSteps(steps, {
		services: isServiceSelectionValid(
			isPartnerView,
			serviceSelection.selectedCustomerId,
			serviceSelection.selectedServiceIds
		),
		details: isTitleValid(title) && isDescriptionValid(description),
		attachments: isKontrolEdge || isContactValid(contact),
	});

	const handleClose = () => {
		setTitle("");
		setDescription("");
		setAttachmentFiles([]);
		setContact(emptyHelpContact());
		serviceSelection.reset();
		stepper.resetSteps();
		submission.resetSubmission();

	resetParticipants();
		onClose();
	};

	const createKontrolEdgeTicket = () => {
		const serviceError = getServiceSelectionError(
			isPartnerView,
			serviceSelection.selectedCustomerId,
			serviceSelection.selectedServiceIds
		);
		if (serviceError) {
			submission.refuse(serviceError);
			return;
		}
		void submission.submit(() =>
			createKontrolTicket(api, {
				requesttype: "HELP_REQUEST",
				summary: title,
				description,
				subscriptionID: serviceSelection.selectedSubscriptionIds,
				serviceType: serviceSelection.selectedServiceTypes,
				end_customer: endCustomerId,
				files: attachmentFiles,
			}),
			addParticipantsOnceCreated(api, participants)
		);
	};

	const createReflectorTicket = () => {
		void submission.submit(() =>
			createTicket(api, {
				Tipo: "Help Request",
				Prioridad: 1,
				Resumen: title,
				Descripcion: description,
				files: attachmentFiles,
				// Help Request has always sent the contact, switch on or not.
				...toContactPayload(contact),
			})
		);
	};

	return (
		<RequestModalShell
			type="help"
			isOpen={isOpen}
			onClose={handleClose}
			steps={steps}
			activeStepId={stepper.activeStepId}
			canActivate={stepper.canActivate}
			onStepChange={stepper.goToStep}
			canContinue={stepper.canContinue}
			isSaving={submission.isSaving}
			onSubmit={isKontrolEdge ? createKontrolEdgeTicket : createReflectorTicket}
			submitError={submission.submitError}
			created={submission.created}
		>
			{stepper.activeStepId === "services" && (
				<KontrolEdgeServiceSelection
					isPartnerView={isPartnerView}
					uniqueCustomers={uniqueCustomers}
					filteredServiceItems={filteredServiceItems}
					selectedCustomerId={serviceSelection.selectedCustomerId}
					selectedCustomerNameDisplay={
						serviceSelection.selectedCustomerNameDisplay
					}
					selectedServiceLabels={serviceSelection.selectedServiceLabels}
					onCustomerSelect={(name) =>
						serviceSelection.handleCustomerSelect(name, uniqueCustomers)
					}
					onServiceSelect={(labels) =>
						serviceSelection.handleServiceSelect(labels, filteredServiceItems)
					}
					servicesHint="The service(s) your question is about."
				/>
			)}

			{stepper.activeStepId === "details" && (
				<SummaryFields
					title={title}
					description={description}
					onTitleChange={setTitle}
					onDescriptionChange={setDescription}
					titlePlaceholder="What do you need help with?"
					descriptionPlaceholder="Your question, with any detail that helps us answer it"
				/>
			)}

			{stepper.activeStepId === "attachments" && (
				<>
					<AttachmentDropzone
						files={attachmentFiles}
						onChange={setAttachmentFiles}
					/>
					{/* Kontrol Edge tickets do not carry a contact, so asking for one there
					    only collected data that was thrown away. */}
					{!isKontrolEdge && (
						<ContactResponseFields contact={contact} onChange={setContact} />
					)}
					{isKontrolEdge && (
						<NewTicketParticipants
							endCustomerId={endCustomerId}
							selected={participants}
							onChange={setParticipants}
						/>
					)}
				</>
			)}
		</RequestModalShell>
	);
};

export default HelpRequestModal;
